import { DetailedDrop_LiveShow_LiveShowState as LiveShowState } from '@aboutyou/legacy-shared/src/grpc_client/grips/protos/shared/detailed_drop'
import DropsTranslationContext from '@aboutyou/legacy-shared/src/pages/Drops/context/TranslationProvider'
import { LiveShoppingPlayerProvider } from '@aboutyou/legacy-shared/src/pages/Live/context/LiveShoppingPlayerProvider'
import { TrackingPageTypeWithId } from '@aboutyou/legacy-tracking-sdk'
import { PageTrackingContextProvider } from 'helpers/tracking/PageTrackingContext'
import { StreamTrackingContext } from 'helpers/tracking/StreamTrackingContext'
import { useTrackingPageUrl } from 'helpers/tracking/hooks/useTrackingPageUrl'
import TrackContentPageView from 'helpers/tracking/trackerComponents/TrackContentPageView'
import { DropDetailsBanner } from 'pages/Drops/components/DropDetailsBanner'
import { ProductTileRowWithoutSorting } from 'pages/Drops/components/ProductTileRowWithoutSorting'
import { useDetailedDropById } from 'pages/Drops/useDetailedDropById'
import { useDropTranslations } from 'pages/Drops/useDropTranslations'
import { useProductIdsForResponsiveRowBuilder } from 'pages/Drops/useProductIdsForResponsiveRowBuilder'
import React, { useMemo, useRef } from 'react'
import { ViewportProvider } from 'sharedComponents/ViewportProvider/ViewportProvider'
import { DropDetailsInformationText } from 'uiComponents/atoms/Drops/DropDetailsInformationText'
import { DropsSectionContainer } from 'uiComponents/atoms/Drops/DropsSectionContainer'
import { LiveShowBanner } from 'uiComponents/atoms/Drops/LiveShowBanner'
import { LiveShows } from 'uiComponents/atoms/Drops/LiveShows'
import RowBuilder, {
  RowTypes,
} from 'uiComponents/molecules/TileGrid/RowBuilder'
import TileGrid from 'uiComponents/molecules/TileGrid/TileGrid'

const rowBuilder = new RowBuilder({ tilesPerRow: 4 })

const DropDetailsPage: React.FC = () => {
  const translations = useDropTranslations()
  const detailedDropById = useDetailedDropById()
  const { path, search } = useTrackingPageUrl()
  const dropDetailsWrapperRef = useRef<HTMLDivElement>(null)

  useProductIdsForResponsiveRowBuilder(rowBuilder)

  const streamTrackingContextValue = useMemo(
    () => ({
      productIndexOffset: 0,
      getProductIndexInStream: rowBuilder.getProductIndexInGridByTileIndex,
      getTilesPerRow: () => rowBuilder.tilesPerRow,
    }),
    [rowBuilder],
  )

  const showLiveShowBanner = useMemo(
    () =>
      detailedDropById?.liveShows.some(
        show => show.state === LiveShowState.LIVE_SHOW_STATE_LIVE,
      ),
    [detailedDropById?.liveShows],
  )

  if (!detailedDropById) {
    return null
  }
  return (
    <PageTrackingContextProvider
      path={path}
      search={search}
      type={TrackingPageTypeWithId.DropDetails}
      id={detailedDropById.id}
    >
      <DropsTranslationContext.Provider value={translations}>
        <TrackContentPageView />
        <article ref={dropDetailsWrapperRef}>
          <DropDetailsBanner dropDetailsWrapperRef={dropDetailsWrapperRef} />
          {showLiveShowBanner && <LiveShowBanner />}
          <DropsSectionContainer>
            <DropDetailsInformationText
              informationText={detailedDropById?.informationText}
            />
            <LiveShoppingPlayerProvider>
              <LiveShows liveShows={detailedDropById.liveShows} />
            </LiveShoppingPlayerProvider>
            <StreamTrackingContext.Provider value={streamTrackingContextValue}>
              <ViewportProvider experimentalSchedulerEnabled>
                <TileGrid
                  rowIndexOffset={0}
                  rowBuilder={rowBuilder}
                  rowComponentMap={{
                    [RowTypes.StreamRow]: ProductTileRowWithoutSorting,
                  }}
                />
              </ViewportProvider>
            </StreamTrackingContext.Provider>
          </DropsSectionContainer>
        </article>
      </DropsTranslationContext.Provider>
    </PageTrackingContextProvider>
  )
}

export default DropDetailsPage
